import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import chroma from "chroma-js";
import { toast } from "react-toastify";
import DataTableSubHeader from "../../../Components/DataTableSubHeader/DataTableSubHeader.jsx";
import { SubHeaderMobileOption } from "../../../Components/SubHeaderMobileOption/SubHeaderMobileOption.jsx";
import {
  AddIcon,
  CrossIcon,
  EditIcon,
  RightArrowIcon,
} from "../../../assets/icons/index.js";
import {
  getUser,
  getRoles,
  getGroups,
  setUserRoles,
  setUserGroups,
  updateAdminPwd,
} from "../../../api/index.js";
import { useAppContext } from "../../../Components/Context/AppContext.jsx";
import userHasPermission from "../../../helpers/formTools/userHasPermission/index.js";
import userOrganizationsHelper from "../../../helpers/formTools/userOrganizations/index.js";
import { getReactSelectThemeProps } from "../../../helpers/reactSelectThemeStyles.js";
import Spinner from "../../../Components/ProgressBar/Spinner.js";
import "./EditUser.scss";

const reactSelectThemeProps = getReactSelectThemeProps();

const colourStyles = {
  control: (styles) => ({ ...styles }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    const color = chroma(data.color || "#000");
    return {
      ...styles,
      backgroundColor: isDisabled
        ? undefined
        : isSelected
        ? data.color
        : isFocused
        ? color.alpha(0.1).css()
        : undefined,
      color: isDisabled
        ? "#ccc"
        : isSelected
        ? chroma.contrast(color, "white") > 2
          ? "white"
          : "black"
        : data.color,
      cursor: isDisabled ? "not-allowed" : "default",
      ":active": {
        ...styles[":active"],
        backgroundColor: !isDisabled
          ? isSelected
            ? data.color
            : color.alpha(0.3).css()
          : undefined,
      },
    };
  },
  multiValue: (styles, { data }) => {
    const color = chroma(data.color || "#000");
    return { ...styles, backgroundColor: color.alpha(0.1).css() };
  },
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: data.color,
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: data.color,
    ":hover": { backgroundColor: data.color, color: "white" },
  }),
};

const areDifferent = (a, b) => {
  const aIds = a?.map((u) => u.value || u.id) || [];
  const bIds = b?.map((u) => u.value || u.id) || [];
  if (aIds.length !== bIds.length) return true;
  const sortedA = [...aIds].sort();
  const sortedB = [...bIds].sort();
  return sortedB.some((id, index) => id !== sortedA[index]);
};

const transformUser = (user) => {
  if (!user) return null;
  const roles = user.roles || [];
  const groups = user.groups || [];
  const safeUser = { ...user, roles, groups };
  const userOrganizations = userOrganizationsHelper(safeUser) || [];
  return {
    username: user.username,
    email: user.email,
    organizations: userOrganizations.map((organization) => ({
      value: organization.id,
      label: organization.name,
      roles: roles
        .filter(
          (role) =>
            role.organization?.id === organization.id ||
            (role.organization === null && organization.id === null)
        )
        .map((role) => ({
          value: role.id,
          label: role.name,
          color: role.color || "#000000",
        })),
      groups: groups
        .filter(
          (group) =>
            group.organization?.id === organization.id ||
            (group.organization === null && organization.id === null)
        )
        .map((group) => ({
          value: group.id,
          label: group.name,
          color: group.color || "#000000",
        })),
    })),
  };
};

function EditUser() {
  const { selectedAdminItem, userProfile } = useAppContext();
  const [isMenuActive, setMenuActive] = useState(false);
  const { pathname } = useLocation();
  const organization = JSON.parse(localStorage.getItem("organization"));
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [transformedUser, setTransformedUser] = useState(null);
  const [rolesData, setRolesData] = useState([]);
  const [groupsData, setGroupsData] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [currentRoles, setCurrentRoles] = useState([]);
  const [currentGroups, setCurrentGroups] = useState([]);
  const [newPassword, setNewPassword] = useState("");
  const [isUpdatePassword, setIsUpdatePassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const userCanWrite = userHasPermission({
    userProfile,
    organization,
    resource: "USER",
    permission: "WRITE",
  });

  const userIsSuperAdmin = userProfile?.roles?.some(
    (role) =>
      role.authorities?.includes("ADMIN") && role.organization === null
  );

  const disabled = pathname !== "/users/edit";

  const applyUser = useCallback((user, preferredOrgId) => {
    setUserData(user);
    const transformed = transformUser(user);
    setTransformedUser(transformed);

    if (transformed?.organizations?.length > 0) {
      const matchedOrg = preferredOrgId != null
        ? transformed.organizations.find((o) => o.value === preferredOrgId)
        : null;
      const org = matchedOrg || transformed.organizations[0];
      setSelectedOrganization({ value: org.value, label: org.label });
    }
  }, []);

  const initFromSelectedItem = useCallback(async () => {
    if (!selectedAdminItem?.username) {
      setIsLoading(false);
      return;
    }

    const clickedOrgId = selectedAdminItem.organization?.id ?? null;

    // Show the split user data immediately
    applyUser(selectedAdminItem, clickedOrgId);
    setIsLoading(true);

    // Fetch the full user (with all organizations) from the API
    try {
      const apiResponse = await getUser(selectedAdminItem.username);
      // httpArcheo.get returns response.data, so apiResponse is the API payload.
      // Try common wrapper shapes: { user: {...} }, { result: {...} }, or direct user object.
      let fullUser = null;
      if (apiResponse?.username) {
        fullUser = apiResponse;
      } else if (apiResponse?.user?.username) {
        fullUser = apiResponse.user;
      } else if (apiResponse?.result?.username) {
        fullUser = apiResponse.result;
      }

      if (fullUser) {
        applyUser(fullUser, clickedOrgId);
      }
    } catch (err) {
      console.error("Error fetching full user data, using list data", err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedAdminItem, applyUser]);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [rolesResponse, groupsResponse] = await Promise.all([
        getRoles({ pageSize: 100, page: 1 }),
        getGroups({ pageSize: 100, page: 1 }),
      ]);

      if (rolesResponse?.results) {
        setRolesData(
          rolesResponse.results.map((role) => ({
            id: role.id,
            value: role.id,
            label: role.name,
            color: role.color || "#000000",
          }))
        );
      }
      if (groupsResponse?.results) {
        setGroupsData(
          groupsResponse.results.map((group) => ({
            value: group.id,
            label: group.name,
            color: group.color || "#000000",
            organization: group.organization,
          }))
        );
      }

      
    } catch (err) {
      console.error("Error fetching reference data", err);
    }
  }, [organization.id]);

  useEffect(() => {
    initFromSelectedItem();
  }, [initFromSelectedItem]);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  useEffect(() => {
    if (!transformedUser || !selectedOrganization) return;
    const orgData = transformedUser.organizations?.find(
      (o) => o.value === selectedOrganization.value
    );
    setCurrentRoles(orgData?.roles || []);
    setCurrentGroups(orgData?.groups || []);
  }, [transformedUser, selectedOrganization]);

  const handleRoleSelectChange = useCallback(
    (selectedRole) => {
      setTransformedUser((prev) => ({
        ...prev,
        organizations: prev.organizations.map((o) =>
          o.value === selectedOrganization.value
            ? { ...o, roles: selectedRole }
            : o
        ),
      }));
    },
    [selectedOrganization]
  );

  const handleGroupSelectChange = useCallback(
    (selectedGroup) => {
      setTransformedUser((prev) => ({
        ...prev,
        organizations: prev.organizations.map((o) =>
          o.value === selectedOrganization.value
            ? { ...o, groups: selectedGroup }
            : o
        ),
      }));
    },
    [selectedOrganization]
  );

  const handleSave = useCallback(async () => {
    if (!userData || !transformedUser) return;
    setIsLoading(true);
    try {
      if (isUpdatePassword && newPassword) {
        await updateAdminPwd({
          username: userData.username,
          newPassword,
        });
      }

      const oldUser = transformUser(userData);
      const allOrgs = [
        ...transformedUser.organizations,
        ...oldUser.organizations,
      ];
      const uniqueOrgIds = [...new Set(allOrgs.map((o) => o.value))];

      let groupsToUpdate = [];

      for (const orgId of uniqueOrgIds) {
        const newOrg = transformedUser.organizations.find(
          (o) => o.value === orgId
        );
        const oldOrg = oldUser.organizations.find((o) => o.value === orgId);

        if (areDifferent(newOrg?.roles, oldOrg?.roles)) {
          await setUserRoles({
            username: userData.username,
            roles: newOrg?.roles?.map((r) => r.value) || [],
            organizationId: orgId,
          });
        }

        groupsToUpdate.push(...newOrg?.groups?.map((g) => g.value) || []);
      }

      await setUserGroups({
        username: userData.username,
        groups: groupsToUpdate,
      });

      toast.success("Utente aggiornato con successo!");
      navigate(-1);
    } catch (err) {
      console.error("Error saving user", err);
      toast.error("Errore nell'aggiornamento dell'utente");
    } finally {
      setIsLoading(false);
    }
  }, [userData, transformedUser, isUpdatePassword, newPassword, navigate]);

  const breadcrumbsData = [
    {
      id: 1,
      label: `Amministrazione ${localStorage.getItem("username")}`,
      iconUrl: true,
    },
    { id: 2, label: "UTENTI", iconUrl: true },
    { id: 3, label: "Dettaglio utente", iconUrl: "" },
  ];

  const subHeaderRightNavOptions = {
    icons: disabled
      ? [
          {
            icon: (
              <RightArrowIcon
                fillColor="var(--archeo-color)"
                className="reverted-edit-icon"
              />
            ),
            onClick: () => navigate(-1),
            label: "Indietro",
          },
          userCanWrite && {
            icon: <EditIcon fillColor="var(--archeo-color)" />,
            onClick: () => navigate("/users/edit"),
            label: "Edita",
          },
        ]
      : [],
    components: disabled
      ? []
      : [
          <button
            key="cancel"
            onClick={() => navigate(-1)}
            className="cancel-button"
          >
            <div className="login-icon">
              <CrossIcon />
            </div>
            <span className="login-text">Annulla</span>
          </button>,
          <button
            key="save"
            onClick={handleSave}
            className="save-button"
          >
            <AddIcon fillColor="#fff" />
            <span className="login-text">Salva</span>
          </button>,
        ],
  };

  if (isLoading) {
    return (
      <div className="container">
        <main className="record-detail-main-content">
          <Spinner />
        </main>
      </div>
    );
  }

  if (!transformedUser) {
    return (
      <div className="container">
        <main className="record-detail-main-content">
          <p>Utente non trovato</p>
        </main>
      </div>
    );
  }

  return (
    <div className="container">
      <main className="record-detail-main-content">
        <DataTableSubHeader
          title="Dettaglio utente"
          breadcrumbsData={breadcrumbsData}
          subHeaderRightNavOptions={subHeaderRightNavOptions}
          isMenuActive={isMenuActive}
          setMenuActive={() => setMenuActive(!isMenuActive)}
        />

        <div className="user-detail-form">
        <div className="blocks-container">
          <div className="form-block" id="user-info">
            <p className="section-title">INFORMAZIONI UTENTE</p>

            <div className="form-row">
              <div className="form-field">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  className="form-input"
                  value={transformedUser.username || ""}
                  disabled
                />
              </div>
              <div className="form-field">
                <label className="form-label">Email</label>
                <input
                  type="text"
                  className="form-input"
                  value={transformedUser.email || ""}
                  disabled
                />
              </div>
            </div>

            {!disabled && (
              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">
                    Nuova Password
                    <button
                      className="pwd-toggle-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsUpdatePassword(!isUpdatePassword);
                      }}
                    >
                      {isUpdatePassword ? (
                        <CrossIcon />
                      ) : (
                        <EditIcon fillColor="var(--archeo-color)" />
                      )}
                    </button>
                  </label>
                  <input
                    type="password"
                    className="form-input"
                    disabled={!isUpdatePassword}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={
                      isUpdatePassword
                        ? "Inserisci nuova password"
                        : "Clicca l'icona per modificare"
                    }
                  />
                </div>
              </div>
            )}
          </div>

          {userIsSuperAdmin && (
            <div className="form-block" id="user-organization">
              <p className="section-title">ORGANIZZAZIONE</p>
              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">Organizzazione</label>
                  {disabled ? (
                    <input
                      type="text"
                      className="form-input"
                      value={selectedOrganization?.label || "NA"}
                      disabled
                    />
                  ) : (
                    <Select
                      {...reactSelectThemeProps}
                      options={transformedUser.organizations}
                      value={selectedOrganization}
                      onChange={setSelectedOrganization}
                      placeholder="Seleziona un'organizzazione"
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="form-block" id="user-roles">
            <p className="section-title">RUOLI</p>
            <div className="form-row">
              <div className="form-field">
                <label className="form-label">Ruoli assegnati</label>
                {disabled ? (
                  <div className="badges-container">
                    {currentRoles.length > 0 ? (
                      currentRoles.map((role) => (
                        <span
                          key={role.value}
                          className="badge"
                          style={{
                            backgroundColor: role.color || "#ccc",
                            color:
                              chroma.contrast(
                                chroma(role.color || "#ccc"),
                                "white"
                              ) > 2
                                ? "#fff"
                                : "#000",
                          }}
                        >
                          {role.label}
                        </span>
                      ))
                    ) : (
                      <span className="no-value-text">Nessun ruolo</span>
                    )}
                  </div>
                ) : (
                  <Select
                    {...reactSelectThemeProps}
                    isMulti
                    options={rolesData}
                    value={currentRoles}
                    onChange={handleRoleSelectChange}
                    placeholder="Seleziona ruoli"
                    styles={colourStyles}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="form-block" id="user-groups">
            <p className="section-title">GRUPPI</p>
            <div className="form-row">
              <div className="form-field">
                <label className="form-label">Gruppi assegnati</label>
                {disabled ? (
                  <div className="badges-container">
                    {currentGroups.length > 0 ? (
                      currentGroups.map((group) => (
                        <span
                          key={group.value}
                          className="badge"
                          style={{
                            backgroundColor: group.color || "#ccc",
                            color:
                              chroma.contrast(
                                chroma(group.color || "#ccc"),
                                "white"
                              ) > 2
                                ? "#fff"
                                : "#000",
                          }}
                        >
                          {group.label}
                        </span>
                      ))
                    ) : (
                      <span className="no-value-text">Nessun gruppo</span>
                    )}
                  </div>
                ) : (
                  <Select
                    {...reactSelectThemeProps}
                    isMulti
                    options={groupsData
                      .filter(
                        (g) =>
                          g.organization?.id === selectedOrganization?.value
                      )
                      .map((g) => ({
                        value: g.value,
                        label: g.label,
                        color: g.color,
                      }))}
                    value={currentGroups}
                    onChange={handleGroupSelectChange}
                    placeholder="Seleziona gruppi"
                    styles={colourStyles}
                  />
                )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {isMenuActive && (
        <SubHeaderMobileOption
          buttons={subHeaderRightNavOptions?.components}
          icons={subHeaderRightNavOptions?.icons}
          onCrossClick={() => setMenuActive(false)}
          title="options"
        />
      )}
      {isLoading && <Spinner />}
    </div>
  );
}

export default EditUser;
